/**
 * Copiar/compartilhar — usado tanto pelo código da liga quanto pelo
 * Ranking. Sempre tenta a Web Share API nativa primeiro (é o que abre o
 * menu "Compartilhar" de verdade do celular — WhatsApp, Telegram, etc.);
 * cai pra copiar na área de transferência em qualquer navegador/contexto
 * que não suporte isso (a maioria dos desktops).
 */
export async function shareOrCopyText(payload: { title?: string; text: string }): Promise<"shared" | "copied" | "failed"> {
  if (navigator.share) {
    try {
      await navigator.share({ title: payload.title, text: payload.text });
      return "shared";
    } catch (err) {
      // Usuário cancelou o menu de compartilhar — não é erro de verdade,
      // não tenta o fallback de copiar em cima disso.
      if (err instanceof Error && err.name === "AbortError") return "failed";
      // Qualquer outro erro real (raro) cai pro fallback de copiar.
    }
  }
  try {
    await navigator.clipboard.writeText(payload.text);
    return "copied";
  } catch {
    return "failed";
  }
}

/** Copia só pra área de transferência, sem tentar o menu de compartilhar
 * (usado pelo botão dedicado "Copiar", separado do "Compartilhar"). */
export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/**
 * Compartilha uma imagem (Blob) + texto, com fallback em cascata:
 * 1. Web Share API com arquivo anexado (a maioria dos apps de celular
 *    aceita isso — WhatsApp mostra a imagem direto na conversa).
 * 2. Web Share API só com texto (celular sem suporte a compartilhar
 *    arquivo, mas com o menu nativo).
 * 3. Copiar o texto (desktop, sem Web Share API nenhuma).
 */
export async function shareImageOrText(payload: {
  title?: string;
  text: string;
  fileName: string;
  imageBlob: Blob | null;
}): Promise<"shared" | "copied" | "failed"> {
  if (payload.imageBlob && navigator.canShare) {
    try {
      const file = new File([payload.imageBlob], payload.fileName, { type: payload.imageBlob.type });
      if (navigator.canShare({ files: [file] })) {
        await navigator.share({ title: payload.title, text: payload.text, files: [file] });
        return "shared";
      }
    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") return "failed";
      // Qualquer outro erro real cai pro fallback abaixo (texto).
    }
  }
  return shareOrCopyText({ title: payload.title, text: payload.text });
}
